import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { compose, pure, withHandlers } from 'recompose';
import Dimensions from 'react-dimensions';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import * as actions from 'actions';
import {
  getSchedulesCollectionByGroup,
  isAtLeastOneAvailableToDeleteByGroup,
  getSchedulesFromExternal,
  requestInProcess,
  getSchedulesFromExternalByGroup,
  getSchedulesCollection,
} from 'selectors';

import ScrollToTopOnMount from 'hocs/ScrollToTopOnMount';
import Loader from './components/Loader';
import { useTableKeyboardNavigation } from './hooks';

import {
  StyledTable,
  TableWrapper,
  TableContainer,
  StyledTbody,
  StyledTd,
  StyledTr,
  StyledThead,
  StyledTh,
  RescheduleTableContainer
} from './styles';

import ActionBar from './components/ActionBar';
import ScheduleModal from './components/SсheduleModal';
import useColumns from './components/Columns/columns';
import { useScheduleTable } from '../../../Application/SchedulePage/context';
import transformContactsToBorrower, {
  emptyRow,
  isRowAtLeastOneCellComplete,
  GROUP_ENUM,
  requiredFields,
  GROUP_COLUMN_CONFIG,
  REQUIRED_FIELDS_CONFIG,
  snakeToCamel
} from './utils';
import NoPropertiesModal from './components/NoPropertiesModal';
import ConfirmationModal from './components/ConfirmationModal';
import CompleteButton from './components/CompleteButton';
import { useScheduleValidation } from './hooks/useScheduleValidation';

const propTypes = {
  externalSchedules: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  collection: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  allSchedulesCollection: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  containerWidth: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]).isRequired,
  group: PropTypes.string.isRequired,
  handleAdd: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleAddressChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  title: PropTypes.string,
  searchClicked: PropTypes.bool,
};

const defaultProps = {
  externalSchedules: [],
  collection: [],
  allSchedulesCollection: [],
  title: undefined,
};

function ScheduleTable({
  containerWidth,
  group,
  handleChange,
  onDelete,
  fetchSchedules,
  collection,
  allSchedulesCollection,
  match,
  targetId,
  targetType,
  batchUpdateSchedules,
  isLoading,
  notifyLoanOfficer,
  externalSchedules,
  isImportLoading,
  fetchSchedulesFromExternalDirectly
}) {
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalWindowVisible, setModalWindowVisible] = useState(false);
  const [noPropertiesModalVisible, setNoPropertiesModalVisible] = useState(false);
  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [_, setEditingRows] = useState(new Set());
  const EMPTY_ROWS_COUNT = 5;

  const tableDOMRef = useRef(null);

  const validation = useScheduleValidation(allSchedulesCollection, validationErrors, requiredFields);

  const scheduleData = useSelector((state) =>
    getSchedulesFromExternalByGroup(state, group)
  );

  const {
    fetchSchedulesFromExternal,
    contacts,
    searchButtonClicked,
    setSearchButtonClicked,
  } = useScheduleTable();

  const allRowIndices = useMemo(() => {
    if (!collection || collection.length === 0) return [];

    return collection
      .map((_, idx) => idx)
      .filter(idx => {
        if (group === 'primary' && idx === 0) {
          return false;
        }
        return true;
      });
  }, [collection, group]);


  const getAllRequiredFieldsForAllGroups = useCallback(() => {
    const allGroups = ['fix_and_flips', 'investment_properties_owned', 'primary_residence'];
    const allRequiredFields = {};

    allGroups.forEach(groupName => {
      allRequiredFields[groupName] = requiredFields(groupName);
    });

    return allRequiredFields;
  }, []);

  const navigation = useTableKeyboardNavigation(collection, GROUP_COLUMN_CONFIG[group], tableDOMRef);

  const validateAllSchedulesCollection = useCallback(() => {
    const realSchedules = allSchedulesCollection.filter(schedule =>
      schedule.id &&
      schedule.id !== "" &&
      !schedule._new &&
      !schedule._emptyRowId &&
      Object.keys(schedule).some(key =>
        !['_new', '_emptyRowId', 'key'].includes(key) &&
        schedule[key] &&
        schedule[key].toString().trim() !== ''
      )
    );

    if (!realSchedules || realSchedules.length === 0) {
      return {
        isValid: false,
        message: 'Please add at least one property to complete the schedule.',
        missingFieldsByGroup: {},
        totalMissingFields: 0
      };
    }

    const allRequiredFields = getAllRequiredFieldsForAllGroups();
    const missingFieldsByGroup = {};
    let totalMissingFields = 0;
    let hasValidationErrors = Object.keys(validationErrors).length > 0;

    const groupedSchedules = realSchedules.reduce((acc, schedule) => {
      const scheduleType = schedule.group;
      if (!scheduleType) {
        return acc;
      }
      if (!acc[scheduleType]) {
        acc[scheduleType] = [];
      }
      acc[scheduleType].push(schedule);
      return acc;
    }, {});

    Object.keys(groupedSchedules).forEach(groupType => {
      const schedules = groupedSchedules[groupType];
      const requiredFieldsForGroup = allRequiredFields[groupType] || [];

      missingFieldsByGroup[groupType] = [];

      schedules.forEach((schedule, index) => {
        requiredFieldsForGroup.forEach(field => {
          const value = schedule[field];
          if (!value || value.toString().trim() === '') {
            missingFieldsByGroup[groupType].push({
              rowIndex: index + 1,
              field: field,
              id: schedule.id
            });
            totalMissingFields++;
          }
        });
      });
    });

    const hasEmptyFields = totalMissingFields > 0;

    return {
      isValid: !hasEmptyFields && !hasValidationErrors,
      message: hasValidationErrors
        ? 'Please fix validation errors before submitting.'
        : hasEmptyFields
          ? 'All required fields must be completed before proceeding.'
          : null,
      missingFieldsByGroup,
      totalMissingFields,
      hasValidationErrors
    };
  }, [allSchedulesCollection, validationErrors, getAllRequiredFieldsForAllGroups]);

  const getDetailedValidationMessage = useCallback(() => {
    const validation = validateAllSchedulesCollection();

    if (!validation.isValid) {
      if (!allSchedulesCollection || allSchedulesCollection.length === 0) {
        return 'Please add at least one property to complete the schedule.';
      }

      if (validation.hasValidationErrors) {
        return 'Please fix validation errors before submitting.';
      }

      if (validation.totalMissingFields > 0) {
        const groupMessages = [];

        Object.keys(validation.missingFieldsByGroup).forEach(groupType => {
          const missingFields = validation.missingFieldsByGroup[groupType];
          if (missingFields.length > 0) {
            const groupDisplayName = getGroupDisplayName(groupType);
            const fieldCount = missingFields.length;
            groupMessages.push(`${groupDisplayName}: ${fieldCount} field${fieldCount > 1 ? 's' : ''} missing`);
          }
        });

        return `All required fields must be completed before proceeding.`;
      }
    }

    return null;
  }, [validateAllSchedulesCollection, allSchedulesCollection]);

  const getGroupDisplayName = (groupType) => {
    const displayNames = {
      'primary': 'Investment Properties Owned',
      'rental': 'Rental',
      'bridge': 'Bridge'
    };
    return displayNames[groupType] || groupType;
  };
  const handleSubmitComplete = useCallback(() => {
    const validation = validateAllSchedulesCollection();
    notifyLoanOfficer(match.params.token);

  }, [validateAllSchedulesCollection, allSchedulesCollection, match.params.token]);

  const handleValidationChange = useCallback((fieldKey, error, hasError) => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      if (hasError && error) {
        newErrors[fieldKey] = error;
      } else {
        delete newErrors[fieldKey];
      }
      return newErrors;
    });
  }, []);

  const isAllSelected = selectedRows.length === allRowIndices.length &&
    allRowIndices.length > 0;

  const data = useMemo(() => {
    if (!collection || collection.length === 0) {
      return Array.from({ length: EMPTY_ROWS_COUNT }, (_, index) => ({
        ...emptyRow,
        _new: true,
        _emptyRowId: `empty_${index}`
      }));
    }

    const cleanedCollection = collection.filter(row =>
      !(row._new === true && (!row.id || row.id === ""))
    );

    const emptyRows = Array.from({ length: EMPTY_ROWS_COUNT }, (_, index) => ({
      ...emptyRow,
      _new: true,
      _emptyRowId: `empty_${cleanedCollection.length + index}`
    }));

    return [
      ...cleanedCollection,
      ...emptyRows
    ];
  }, [collection]);

  const enhancedHandleChange = useCallback((value, column, record, isValid = true) => {
    const rowIndex = data.findIndex(row =>
      row.key === record.key ||
      (row.id && row.id === record.id) ||
      row._emptyRowId === record._emptyRowId ||
      row === record
    );

    const isEmptyRow = record._new === true;
    const hasValue = value && value.toString().trim() !== '';

    if (!hasValue) {
      const recordWithClearedField = { ...record, [snakeToCamel(column)]: '' };
      const fields = requiredFields(GROUP_ENUM[group])
      const isRowCompletelyEmpty = !isRowAtLeastOneCellComplete(recordWithClearedField, fields);

      if (!isEmptyRow && record.id && isRowCompletelyEmpty) {
        if (rowIndex !== 0 && group !== 'primary') {
          onDelete(record);
          return;
        }
      }
    }

    if (isEmptyRow && hasValue) {
      setEditingRows(prev => new Set([...prev, rowIndex]));
    }

    const fieldKey = `${record.key || record.id || record._emptyRowId}_${column}`;

    if (!isValid) {
      return;
    }

    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldKey];
      return newErrors;
    });

    handleChange(value, column, record);
  }, [data, handleChange, onDelete]);

  const columns = useMemo(() => {
    return useColumns({
      group,
      scheduleData,
      handleChange: enhancedHandleChange,
      setSelectedRows,
      allRowIndices,
      onValidationChange: handleValidationChange,
      selectedRows,
      isAllSelected,
      isFieldMissing: validation.isFieldMissing,
      navigation,
    });
  }, [
    scheduleData,
    allRowIndices,
    isAllSelected,
    handleValidationChange,
    selectedRows,
    group,
    enhancedHandleChange,
    validation,
    navigation,
  ]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
  });


  useEffect(() => {
    fetchSchedules(match.params.token);
    if (searchButtonClicked) {
      const { isValid } = validateAllSchedulesCollection();

      if (!isValid) {
        setModalWindowVisible(true);
      } else {
        fetchSchedulesFromExternalDirectly(
          match.params.token,
          transformContactsToBorrower(contacts),
        )
          .then((response) => {
            if (response.collection.properties.length === 0) {
              setNoPropertiesModalVisible(true);
            }
            fetchSchedules(match.params.token);
          })
        setSearchButtonClicked(false);
      }
    }
  }, [
    searchButtonClicked,
    requiredFields,
    selectedRows,
    table,
    fetchSchedules,
    match.params.token,
    fetchSchedulesFromExternal,
    setSearchButtonClicked,
    isImportLoading
  ]);



  const handleMoveToRental = async () => {
    const selectedItems = selectedRows.map((idx) => data[idx]);
    await batchUpdateSchedules(
      match.params.token,
      selectedItems,
      'group',
      'investment_properties_owned',
      targetId,
      targetType
    );
    setSelectedRows([]);
    fetchSchedules(match.params.token);

  };

  const handleMoveToBridge = async () => {
    const selectedItems = selectedRows.map((idx) => data[idx]);
    await batchUpdateSchedules(
      match.params.token,
      selectedItems,
      'group',
      'fix_and_flips',
      targetId,
      targetType
    );
    setSelectedRows([]);
    fetchSchedules(match.params.token);
  };

  const handleMoveToPrimary = async () => {
    const selectedItems = selectedRows.map((idx) => data[idx]);
    await batchUpdateSchedules(
      match.params.token,
      selectedItems,
      'group',
      'primary_residence',
      targetId,
      targetType
    );
    setSelectedRows([]);
    fetchSchedules(match.params.token);
  };

  const handleNotMine = () => {
    if (selectedRows.length > 0) {
      setConfirmationModalVisible(true);
    }
  };

  const handleConfirmNotMine = async () => {
    const selectedItems = selectedRows.map((idx) => data[idx]);

    try {
      await Promise.all(selectedItems.map((item) => onDelete(item)));
      setConfirmationModalVisible(false);
      await fetchSchedules(match.params.token);
      setSelectedRows([]);
    } catch (error) {
      setConfirmationModalVisible(false);
    }
  };
  const handleCancelNotMine = () => {
    setConfirmationModalVisible(false);
  };

  const onAppend = () => {
    setModalWindowVisible(false);
    fetchSchedulesFromExternalDirectly(
      match.params.token,
      transformContactsToBorrower(contacts)
    )
      .then((response) => {
        if (response.collection.properties.length === 0) {
          setNoPropertiesModalVisible(true);
        }
        fetchSchedules(match.params.token);
      })
    setSearchButtonClicked(false);
  };

  const onReplace = () => {
    setModalWindowVisible(false);
    fetchSchedulesFromExternalDirectly(
      match.params.token,
      transformContactsToBorrower(contacts),
      true
    )
      .then((response) => {
        if (response.collection.properties.length === 0) {
          setNoPropertiesModalVisible(true);
        }
        fetchSchedules(match.params.token);
      })
    setSearchButtonClicked(false);
  };

  const onCancel = () => {
    setModalWindowVisible(false);
    setSearchButtonClicked(false);
  };

  const handleCloseModal = () => {
    setModalWindowVisible(false);
    setSearchButtonClicked(false);
  };

  const handleCloseNoPropertiesModal = () => {
    setNoPropertiesModalVisible(false);
  };


  return (
    <>
      {modalWindowVisible && (
        <ScheduleModal
          onAppend={onAppend}
          onReplace={onReplace}
          onCancel={onCancel}
          onClose={handleCloseModal}
        />
      )}

      {confirmationModalVisible && (
        <ConfirmationModal
          onProceed={handleConfirmNotMine}
          onCancel={handleCancelNotMine}
        />
      )}

      {noPropertiesModalVisible && (
        <NoPropertiesModal
          onClose={handleCloseNoPropertiesModal}
        />
      )}

      <RescheduleTableContainer>
        <ActionBar
          onMoveToRental={handleMoveToRental}
          onMoveToBridge={handleMoveToBridge}
          onMoveToPrimary={handleMoveToPrimary}
          onNotMine={handleNotMine}
          group={group}
          disabled={isLoading || selectedRows.length === 0}
        />

        <TableContainer>
          {(isLoading || isImportLoading) && <Loader />}
          <TableWrapper
            tabIndex={0} style={{ width: containerWidth }}>
            <StyledTable>
              <StyledThead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <StyledTr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <StyledTh
                        key={header.id}
                        style={{
                          width: header.getSize(),
                          position: 'relative'
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getCanResize() && (
                          <div
                            {...{
                              onMouseDown: header.getResizeHandler(),
                              onTouchStart: header.getResizeHandler(),
                              className: `resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`,
                              style: {
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                height: '100%',
                                width: '5px',
                                background: 'rgba(0, 0, 0, 0.5)',
                                cursor: 'col-resize',
                                userSelect: 'none',
                                touchAction: 'none',
                                opacity: header.column.getIsResizing() ? 1 : 0,
                              },
                            }}
                          />
                        )}
                      </StyledTh>
                    ))}
                  </StyledTr>
                ))}
              </StyledThead>

              <StyledTbody>
                {table.getRowModel().rows.map((row) => (
                  <StyledTr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <StyledTd
                        key={cell.id}
                        style={{
                          width: cell.column.getSize()
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </StyledTd>
                    ))}
                  </StyledTr>
                ))}
              </StyledTbody>
            </StyledTable>
          </TableWrapper>
        </TableContainer>
        <CompleteButton
          data={allSchedulesCollection}
          requiredFields={getAllRequiredFieldsForAllGroups()}
          onSubmit={handleSubmitComplete}
          customValidation={validateAllSchedulesCollection}
          customValidationMessage={getDetailedValidationMessage}
        />
      </RescheduleTableContainer>
    </>
  );
}

ScheduleTable.propTypes = propTypes;
ScheduleTable.defaultProps = defaultProps;

function mapStateToProps(state, props) {
  return {
    externalSchedules: getSchedulesFromExternal(state),
    collection: getSchedulesCollectionByGroup(state, props.group),
    allSchedulesCollection: getSchedulesCollection(state),
    isAtLeastOneAvailableToDeleteByGroupCollection: isAtLeastOneAvailableToDeleteByGroup(state, props),
    isLoading: requestInProcess(state, "SCHEDULES"),
    isImportLoading: requestInProcess(state, "SCHEDULES_IMPORT"),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    fetchSchedules: bindActionCreators(actions.fetchSchedules, dispatch),
    fetchSchedulesFromExternalDirectly: bindActionCreators(actions.fetchSchedulesFromExternalDirectly, dispatch),
    batchUpdateSchedules: bindActionCreators(actions.batchUpdateSchedules, dispatch),
    removeApplicationScheduleRequest: bindActionCreators(actions.removeApplicationScheduleRequest, dispatch),
    addApplicationScheduleRequest: bindActionCreators(actions.addApplicationScheduleRequest, dispatch),
    editApplicationScheduleRequest: bindActionCreators(actions.editApplicationScheduleRequest, dispatch),
    notifyLoanOfficer: bindActionCreators(actions.notifyLoanOfficer, dispatch),
  };
}

const enhance = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withHandlers({
    onDelete: ({ target, removeApplicationScheduleRequest, match }) => (record) => {
      const { token } = match.params;
      removeApplicationScheduleRequest(target, token, record);
    },

    handleChange: ({
      target,
      editApplicationScheduleRequest,
      targetType,
      targetId,
      match,
      group,
      addApplicationScheduleRequest
    }) => (value, column, record) => {
      const { token } = match.params;
      const property = record;

      if (record._new || record.new) {
        addApplicationScheduleRequest(target, property);
      }

      if (!record.id) {
        property[column] = value;
        property.group = GROUP_ENUM[group];
      }

      editApplicationScheduleRequest(
        target,
        property,
        column,
        value,
        targetId,
        targetType,
        token
      );
    },
  }),
  pure,
);

export default enhance(ScheduleTable);
